import React, { useState, useEffect } from 'react';
import './AdminMessages.css';
import contactService from '../../../services/contact.service';
import { useToast } from '../../../context/ToastContext';

const AdminMessages = () => {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    resolved: 0,
    unread: 0
  });

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const result = await contactService.getRecentMessages(100);
      if (result.success) {
        setMessages(result.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await contactService.getRecentMessages(1000);
      if (result.success) {
        const msgs = result.messages;
        setStats({
          total: msgs.length,
          new: msgs.filter(m => m.status === 'new').length,
          inProgress: msgs.filter(m => m.status === 'in-progress').length,
          resolved: msgs.filter(m => m.status === 'resolved').length,
          unread: msgs.filter(m => !m.read).length
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      const result = await contactService.updateMessageStatus(messageId, newStatus);
      if (result.success) {
        setMessages(prev => 
          prev.map(message => 
            message.id === messageId ? { ...message, status: newStatus } : message
          )
        );
        await fetchStats();
        toast.showSuccess('Statut mis à jour avec succès');
      }
    } catch (error) {
      console.error('Error updating message status:', error);
      toast.showError('Erreur lors de la mise à jour');
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      const result = await contactService.markAsRead(messageId);
      if (result.success) {
        setMessages(prev => 
          prev.map(message => 
            message.id === messageId ? { ...message, read: true } : message
          )
        );
        await fetchStats();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (messageId) => {
    if (!deleteConfirm) {
      setDeleteConfirm(messageId);
      return;
    }

    try {
      const result = await contactService.deleteMessage(messageId);
      if (result.success) {
        setMessages(prev => prev.filter(message => message.id !== messageId));
        await fetchStats();
        toast.showSuccess('Message supprimé avec succès');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.showError('Erreur lors de la suppression');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const viewMessageDetails = (message) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
    if (!message.read) {
      handleMarkAsRead(message.id);
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesStatus = filterStatus === 'all' || message.status === filterStatus;
    const matchesType = filterType === 'all' || message.type === filterType;
    const matchesSearch = 
      message.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.message?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesType && matchesSearch;
  });

  const getStatusLabel = (status) => {
    switch(status) {
      case 'new': return 'Nouveau';
      case 'in-progress': return 'En cours';
      case 'resolved': return 'Résolu';
      default: return status;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'product': return 'Produit';
      case 'support': return 'Support';
      case 'general': return 'Général';
      default: return type;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-DZ', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-messages-admin-messages">

      {/* Stats Cards */}
      <div className="admin-messages-stats-grid">
        <div className="admin-messages-stat-card admin-messages-stat-card--total">
          <div className="admin-messages-stat-value">{stats.total}</div>
          <div className="admin-messages-stat-label">Total Messages</div>
        </div>
        
        <div className="admin-messages-stat-card admin-messages-stat-card--new">
          <div className="admin-messages-stat-value">{stats.new}</div>
          <div className="admin-messages-stat-label">Nouveaux</div>
        </div>
        
        <div className="admin-messages-stat-card admin-messages-stat-card--progress">
          <div className="admin-messages-stat-value">{stats.inProgress}</div>
          <div className="admin-messages-stat-label">En Cours</div>
        </div>
        
        <div className="admin-messages-stat-card admin-messages-stat-card--unread">
          <div className="admin-messages-stat-value">{stats.unread}</div>
          <div className="admin-messages-stat-label">Non Lus</div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-messages-messages-filters">
        <div className="admin-messages-filter-search">
          <svg className="admin-messages-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher par nom, email ou sujet..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="admin-messages-search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="admin-messages-filter-select"
        >
          <option value="all">Tous les statuts</option>
          <option value="new">Nouveaux</option>
          <option value="in-progress">En cours</option>
          <option value="resolved">Résolus</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="admin-messages-filter-select"
        >
          <option value="all">Tous les types</option>
          <option value="product">Produit</option>
          <option value="support">Support</option>
          <option value="general">Général</option>
        </select>
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="admin-messages-loading-state">
          <div className="admin-messages-spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="admin-messages-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <h3>Aucun message trouvé</h3>
          <p>Essayez de modifier vos filtres</p>
        </div>
      ) : (
        <div className="admin-messages-messages-list">
          {filteredMessages.map(message => (
            <div key={message.id} className={`admin-messages-message-card ${!message.read ? 'admin-messages-message-card--unread' : ''}`}>
              <div className="admin-messages-message-header">
                <div className="admin-messages-message-author-info">
                  <div className="admin-messages-author-avatar">
                    {message.name?.substring(0, 2).toUpperCase() || 'AN'}
                  </div>
                  <div>
                    <div className="admin-messages-author-name">
                      {message.name}
                      {!message.read && <span className="admin-messages-unread-badge">Non lu</span>}
                    </div>
                    <div className="admin-messages-author-email">{message.email}</div>
                  </div>
                </div>
                
                <div className="admin-messages-status-badges">
                  <span className={`status-badge status-badge--${message.status}`}>
                    {getStatusLabel(message.status)}
                  </span>
                  <span className="admin-messages-type-badge">
                    {getTypeLabel(message.type)}
                  </span>
                </div>
              </div>

              <div className="admin-messages-message-body">
                <div className="admin-messages-message-subject">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  {message.subject}
                </div>
                
                <p className="admin-messages-message-text">{message.message}</p>

                <div className="admin-messages-message-meta">
                  {message.phone && (
                    <div className="admin-messages-meta-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {message.phone}
                    </div>
                  )}
                  <div className="admin-messages-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDate(message.createdAt)}
                  </div>
                </div>
              </div>

              <div className="admin-messages-message-actions">
                {message.status === 'new' && (
                  <button
                    onClick={() => handleStatusChange(message.id, 'in-progress')}
                    className="admin-messages-btn-action admin-messages-btn-action--progress"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 11 12 14 22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                    Traiter
                  </button>
                )}
                
                {message.status === 'in-progress' && (
                  <button
                    onClick={() => handleStatusChange(message.id, 'resolved')}
                    className="admin-messages-btn-action admin-messages-btn-action--resolve"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Résoudre
                  </button>
                )}
                
                {message.status === 'resolved' && (
                  <button
                    onClick={() => handleStatusChange(message.id, 'new')}
                    className="admin-messages-btn-action admin-messages-btn-action--secondary"
                  >
                    Rouvrir
                  </button>
                )}

                <button
                  onClick={() => viewMessageDetails(message)}
                  className="admin-messages-btn-action admin-messages-btn-action--secondary"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Détails
                </button>

                <button
                  onClick={() => handleDelete(message.id)}
                  className={`admin-messages-btn-action admin-messages-btn-action--delete ${deleteConfirm === message.id ? 'admin-messages-btn-action--confirm' : ''}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  {deleteConfirm === message.id ? 'Confirmer ?' : 'Supprimer'}
                </button>
                
                {deleteConfirm === message.id && (
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="admin-messages-btn-action admin-messages-btn-action--secondary"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedMessage && (
        <div className="admin-messages-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="admin-messages-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-messages-modal-header">
              <h2>Détails du Message</h2>
              <button className="admin-messages-modal-close" onClick={() => setShowDetailModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="admin-messages-modal-body">
              <div className="admin-messages-detail-author">
                <div className="admin-messages-detail-avatar">
                  {selectedMessage.name?.substring(0, 2).toUpperCase() || 'AN'}
                </div>
                <div>
                  <div className="admin-messages-detail-name">{selectedMessage.name}</div>
                  <div className="admin-messages-detail-email">{selectedMessage.email}</div>
                  {selectedMessage.phone && (
                    <div className="admin-messages-detail-phone">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {selectedMessage.phone}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-messages-detail-section">
                <div className="admin-messages-detail-label">TYPE & STATUT</div>
                <div className="admin-messages-detail-badges">
                  <span className={`admin-messages-status-badge admin-messages-status-badge--${selectedMessage.status}`}>
                    {getStatusLabel(selectedMessage.status)}
                  </span>
                  <span className="admin-messages-type-badge">
                    {getTypeLabel(selectedMessage.type)}
                  </span>
                </div>
              </div>

              <div className="admin-messages-detail-section">
                <div className="admin-messages-detail-label">SUJET</div>
                <div className="admin-messages-detail-value admin-messages-detail-subject">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  {selectedMessage.subject}
                </div>
              </div>

              <div className="admin-messages-detail-section">
                <div className="admin-messages-detail-label">MESSAGE</div>
                <div className="admin-messages-detail-message">{selectedMessage.message}</div>
              </div>

              <div className="admin-messages-detail-section">
                <div className="admin-messages-detail-label">DATE DE RÉCEPTION</div>
                <div className="admin-messages-detail-value">
                  {formatDate(selectedMessage.createdAt)}
                </div>
              </div>
            </div>

            <div className="admin-messages-modal-footer">
              {selectedMessage.status !== 'resolved' && (
                <button
                  onClick={() => {
                    handleStatusChange(selectedMessage.id, 'resolved');
                    setShowDetailModal(false);
                  }}
                  className="admin-messages-btn admin-messages-btn-resolve"
                >
                  Marquer comme résolu
                </button>
              )}
              <button
                onClick={() => window.location.href = `mailto:${selectedMessage.email}`}
                className="admin-messages-btn btn-reply"
              >
                Répondre par email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;